/**
 * --------------------------------------------------------------------------
 * AutoScroller Web Component
 * --------------------------------------------------------------------------
 * This component creates a smooth, continuous scrolling (marquee) effect.
 * It dynamically duplicates content to ensure a seamless loop and pauses
 * the animation when the element is not visible.
 */

if (!customElements.get('auto-scroller')) {
    class AutoScroller extends HTMLElement {
        static CONSTANTS = {
            SCROLL_CONTAINER_SELECTOR: '[data-scroll-content]',
            ORIGINAL_CONTENT_SELECTOR: '[data-original-content]',
            DEFAULT_SCROLL_SPEED: 50, // pixels per second
            DEFAULT_SCROLL_DIRECTION: -1, // -1 for left, 1 for right
            RESIZE_DEBOUNCE_DELAY: 150, // ms
            INTERSECTION_THRESHOLD: 0.01,
        };

        constructor() {
            super();
            this.scrollContainer = null;
            this.originalContentElement = null;
            this.scrollSpeed = AutoScroller.CONSTANTS.DEFAULT_SCROLL_SPEED;
            this.scrollDirection = AutoScroller.CONSTANTS.DEFAULT_SCROLL_DIRECTION;
            this.isVisible = true;
            this.intersectionObserver = null;
            this.resizeObserver = null;
            this.handleResize = null; // To hold the debounced function reference
            this.imagesLoaded = false;
            this.finalCalculationDone = false;
        }

        connectedCallback() {
            if (this.initializeScroller()) {
                this.setupObservers();
            }
        }

        disconnectedCallback() {
            this.cleanup();
        }

        // --------------------------------------------------------------------
        // SETUP & CLEANUP
        // --------------------------------------------------------------------

        /**
         * Initializes the component, validates required elements, and sets up properties.
         * @returns {boolean} True if initialization is successful, false otherwise.
         */
        initializeScroller() {
            this.scrollContainer = this.querySelector(AutoScroller.CONSTANTS.SCROLL_CONTAINER_SELECTOR);
            this.originalContentElement = this.querySelector(AutoScroller.CONSTANTS.ORIGINAL_CONTENT_SELECTOR);
            
            if (!this.scrollContainer) {
                console.error(`AutoScroller Error: The required scroll container ('${AutoScroller.CONSTANTS.SCROLL_CONTAINER_SELECTOR}') was not found.`, this);
                return false;
            }
            if (!this.originalContentElement) {
                console.error(`AutoScroller Error: The required original content element ('${AutoScroller.CONSTANTS.ORIGINAL_CONTENT_SELECTOR}') was not found.`, this);
                return false;
            }

            // Store speed and direction from data attributes or use defaults
            this.scrollSpeed = parseInt(this.dataset.scrollSpeed, 10) || AutoScroller.CONSTANTS.DEFAULT_SCROLL_SPEED;
            this.scrollDirection = parseInt(this.dataset.scrollDirection, 10) || AutoScroller.CONSTANTS.DEFAULT_SCROLL_DIRECTION;

            // Initial setup with temporary calculation
            this.ensureSufficientContent();
            this.updateAnimation();

            // Wait for images to load before final calculation
            this.waitForImagesAndCalculate();

            // Recalculate after fonts have loaded to prevent incorrect width measurements
            if (document.fonts?.ready) {
                document.fonts.ready.then(() => {
                    if (!this.finalCalculationDone) {
                        this.waitForImagesAndCalculate();
                    }
                });
            }
            
            return true;
        }

        /**
         * Removes all observers and event listeners.
         */
        cleanup() {
            if (this.intersectionObserver) this.intersectionObserver.disconnect();
            if (this.resizeObserver) this.resizeObserver.disconnect();
            if (this.handleResize) window.removeEventListener('resize', this.handleResize);
        }

        // --------------------------------------------------------------------
        // CONTENT & ANIMATION MANAGEMENT
        // --------------------------------------------------------------------

        /**
         * Ensures there is enough content to create a seamless scrolling effect
         * by duplicating the original content as needed.
         */
        ensureSufficientContent() {
            if (!this.originalContentElement) return;

            // Force reflow and get consistent width measurement
            this.originalContentElement.offsetWidth;
            const screenWidth = window.innerWidth;
            const singleContentWidth = this.originalContentElement.offsetWidth;
            
            if (singleContentWidth <= 0) return;

            // Calculate how many times the content needs to be repeated.
            // We need at least 2 copies, or enough to fill the screen plus two buffers.
            const requiredWidth = screenWidth + (singleContentWidth * 2);
            const timesToRepeat = Math.max(2, Math.ceil(requiredWidth / singleContentWidth));
            
            
            // Use a DocumentFragment for performance to minimize DOM mutations.
            const fragment = document.createDocumentFragment();
            fragment.appendChild(this.originalContentElement.cloneNode(true)); // Append the original first

            for (let i = 1; i < timesToRepeat; i++) {
                const clone = this.originalContentElement.cloneNode(true);
                clone.removeAttribute('data-original-content'); // Keep selector on the first item only
                fragment.appendChild(clone);
            }
            
            // Replace container content in a single operation.
            this.scrollContainer.innerHTML = '';
            this.scrollContainer.appendChild(fragment);

            // Re-select the original content element after replacing the DOM.
            this.originalContentElement = this.querySelector(AutoScroller.CONSTANTS.ORIGINAL_CONTENT_SELECTOR);
        }

        /**
         * Checks if all images in the content are loaded
         * @returns {boolean} True if all images are loaded or no images present
         */
        areAllImagesLoaded() {
            const images = this.originalContentElement.querySelectorAll('img');
            if (images.length === 0) return true;
            
            return Array.from(images).every(img => {
                return img.complete && img.naturalWidth > 0;
            });
        }

        /**
         * Waits for all images to load and then performs final calculation
         */
        waitForImagesAndCalculate() {
            if (this.finalCalculationDone) return;
            
            const images = this.originalContentElement.querySelectorAll('img');
            if (images.length === 0) {
                this.performFinalCalculation();
                return;
            }
            
            let loadedCount = 0;
            const totalImages = images.length;
            
            const checkImageLoad = () => {
                loadedCount++;
                
                if (loadedCount === totalImages) {
                    this.imagesLoaded = true;
                    this.performFinalCalculation();
                }
            };
            
            images.forEach(img => {
                if (img.complete && img.naturalWidth > 0) {
                    checkImageLoad();
                } else {
                    img.addEventListener('load', checkImageLoad);
                    img.addEventListener('error', checkImageLoad);
                }
            });
        }

        /**
         * Performs the final calculation after images are loaded
         */
        performFinalCalculation() {
            if (this.finalCalculationDone) return;
            
            this.finalCalculationDone = true;
            
            // Force reflow and get final width measurement
            this.originalContentElement.offsetWidth;
            const finalWidth = this.originalContentElement.offsetWidth;
            
            this.ensureSufficientContent();
            this.updateAnimation(finalWidth);
        }

        /**
         * Updates the CSS animation properties based on the content width and scroll speed.
         * @param {number | null} [width=null] - An optional width to use for calculation.
         */
        updateAnimation(width = null) {
            if (!this.originalContentElement) return;

            // Force reflow and get consistent width measurement
            this.originalContentElement.offsetWidth;
            const singleContentWidth = width || this.originalContentElement.offsetWidth;
            if (singleContentWidth <= 0) return;

            const duration = singleContentWidth / this.scrollSpeed;
            const scrollDistance = this.scrollDirection * singleContentWidth;
            
            this.scrollContainer.style.setProperty('--scroll-distance', `${scrollDistance}px`);
            this.scrollContainer.style.animation = `scroll-marquee ${duration}s linear infinite`;
            this.scrollContainer.style.animationPlayState = this.isVisible ? 'running' : 'paused';
        }

        // --------------------------------------------------------------------
        // OBSERVERS
        // --------------------------------------------------------------------

        /**
         * Sets up all necessary observers for visibility and resizing.
         */
        setupObservers() {
            // Observer for handling visibility (pausing animation)
            this.intersectionObserver = new IntersectionObserver(
                (entries) => {
                    const entry = entries[0];
                    const wasVisible = this.isVisible;
                    this.isVisible = entry.isIntersecting;
                    
                    if (wasVisible !== this.isVisible) {
                        this.scrollContainer.style.animationPlayState = this.isVisible ? 'running' : 'paused';
                    }
                },
                { threshold: AutoScroller.CONSTANTS.INTERSECTION_THRESHOLD }
            );
            this.intersectionObserver.observe(this);

            // Observer for changes to the content's width (e.g., font loading)
            if (this.originalContentElement) {
                this.resizeObserver = new ResizeObserver((entries) => {
                    const entry = entries[0];
                    const newWidth = entry.contentRect.width;
                    
                    // Only update if final calculation hasn't been done yet
                    if (newWidth > 0 && !this.finalCalculationDone) {
                        this.updateAnimation(newWidth);
                    }
                });
                this.resizeObserver.observe(this.originalContentElement);
            }

            // Listener for window resize events
            this.handleResize = theme.utils.debounce(() => {
                this.finalCalculationDone = false; // Allow recalculation on window resize
                this.ensureSufficientContent();
                this.waitForImagesAndCalculate();
            }, AutoScroller.CONSTANTS.RESIZE_DEBOUNCE_DELAY);
            window.addEventListener('resize', this.handleResize);
        }
    }

    customElements.define('auto-scroller', AutoScroller);
}